/* eslint-disable no-console */

const STRINGS = {
  finish: "Adding missing $type mark for `$src`, measure will be to window.onload"
};

/**
 * @private
 * Replaces placeholders in message string with values.
 *
 * @param {Object} arg
 * @param {String} arg.string - The message string to use
 * @param {String} arg.src - The source for the message string
 * @param {String} arg.type - The type for the message string
 *
 * @returns {String}
 */
function stringReplace({ string, src, type }) {
  const source = STRINGS[string];

  return source ? source.replace("$src", src).replace("$type", type) : string;
}

/**
 * @private
 * Writes out to the console the grouped marks or measures that have been
 * created in the instance.
 *
 * @param {string} type - Type of entries to use ("marks" / "measures")
 */
function debugReport(type) {
  const self = this;
  const entries = self[type] || [];

  if (!entries.length) {
    console.error(`No ${type} found`);
    return;
  }

  console.group(`${self.src}: ${type}`);
  entries.forEach(entry => {
    const time = type === "marks" ? entry.startTime : entry.duration;

    console.info(`${entry.name}: ${time}ms`);
  });
  console.groupEnd();
}

/**
 * @private
 * Gets the latest entry for the supplied source and type.
 *
 * @param {string} src The source
 * @param {string} type The type of entry to use
 */
function getLatestEntry(src, type) {
  return window.performance
    .getEntriesByType(type)
    .reverse()
    .find(entry => entry.name.substr(0, src.length) === src);
}

/**
 * @private
 * Gets the prefix for marks and measures from the supplied source and optional method.
 *
 * @param {string} src - The source
 * @param {stirng=} method - Name of method (optional)
 */
function getPrefix(src, method) {
  return src + (method ? `:${method}` : "");
}

/**
 * Processes a mark to find an associated finish mark and add a measure of them.
 *
 * Should a finish mark not be found, the `finish` method is run to add one.
 *
 * @private
 *
 * @param {PerformanceEntry} mark - Mark to process
 */
function processMark(mark) {
  const self = this;
  const [method] = mark.split("_");
  const markFinish = `${method}_finish`;

  if (!self.marks.find(({ name }) => name.endsWith(markFinish))) {
    console.warn(stringReplace({ string: STRINGS.finish, src: method, type: "finish" }));

    self.finish(method);
  }

  self.measure(method);
}

/**
 * Verifies that each start mark for methods has a matching finish mark
 *
 * @private
 */
function checkMarks() {
  const self = this;

  self.marks
    .filter(({ name }) => name.endsWith("_start"))
    .map(entry => entry.name.split(":")[1])
    .forEach(processMark.bind(self));
}

function onload() {
  const self = this;
  const { src } = self;
  const finishMark = window.performance
    .getEntriesByType("mark")
    .find(entry => entry.name === `${src}_complete`);

  if (!finishMark) {
    console.warn(stringReplace({ string: STRINGS.finish, src, type: "complete" }));
    self.finish();
  }

  self.measure();
  checkMarks.call(self);
}

const API = {
  /**
   * Adds a mark to denote the finish of method timing.
   *
   * Should `method` be omitted, default behaviour is to set a mark for the
   * finish of the _source_ timing.
   *
   * @param {string=} method - Name of method that is being timed (optional)
   */
  finish: function finish(method) {
    const self = this;
    const { src } = self;
    const prefix = getPrefix(src, method);
    const name = `${prefix}_${method ? "finish" : "complete"}`;

    window.performance.mark(name);
    self.marks = self.marks || [];
    self.marks.push(getLatestEntry.call(self, src, "mark"));
  },

  /**
   * Adds a measure denoting the duration of a supplied method.
   *
   * Should `method` be omitted, default behaviour is to set a measure for the _source_.
   *
   * @param {string=} method - Name of method that is being measured (optional)
   */
  measure: function measure(method) {
    const self = this;
    const { src } = self;
    const name = getPrefix(src, method);
    const startName = `${name}_${method ? "start" : "init"}`;
    const finishName = `${name}_${method ? "finish" : "complete"}`;

    window.performance.measure(name, startName, finishName);
    self.measures = self.measures || [];
    self.measures.push(getLatestEntry.call(self, src, "measure"));
  },

  /**
   * Runs an optionally supplied function to create a report of marks or measures.
   *
   * Should a `type` value not be specified, a default of `"measures"` is used.
   * Should a function reference not be specified, the debugging will automatically occur.
   *
   * @param {Object=} arg
   * @param {Function=} arg.fn - The function to run (optional)
   * @param {String=} arg.type - The type of entry to report on (optional)
   * @param {Boolean=} arg.debug - Flag denoting if debug information should be written
   *                               into the console (optional)
   */
  report: function report({ fn, type = "measures", debug = !fn } = {}) {
    const self = this;

    if (fn) {
      fn(self[type]);
    }

    if (debug) {
      self.debug(type);
    }
  },

  /**
   * Adds a mark to denote the start of method timing.
   *
   * Should `method` be omitted, default behaviour is to set a mark for the start
   * of the _source_ timing.
   *
   * @param {String=} method - Name of method that is being timed (optional)
   */
  start: function start(method) {
    const self = this;
    const { src } = self;
    const prefix = getPrefix(src, method);
    const name = `${prefix}_${method ? "start" : "init"}`;

    window.performance.mark(name);
    self.marks = self.marks || [];
    self.marks.push(getLatestEntry.call(self, src, "mark"));
  }
};

/**
 * When initialised, an event listener is added to the onload event of `window`
 * and a performance mark is immediately added to set the time of initialisation.
 *
 * Should a `src` value not be specified, a default of`"undefined source"` is used.
 *
 * @param {String=} src - Name of source file / base object (optional)
 */
function UserTiming({ src }) {
  const self = this;

  self.src = src || "undefined source";

  if (src) {
    window.addEventListener("load", onload.bind(self));
  }

  self.start();
}

const { prototype: proto } = UserTiming;

Object.keys(API).forEach(key => {
  Object.defineProperty(proto, key, {
    enumerable: true,
    value: API[key]
  });
});

Object.defineProperties(proto, {
  debug: {
    value: debugReport
  }
});

export default UserTiming;



// WEBPACK FOOTER //
// ./shared/helpers/user-timing.js